import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { Line } from 'react-chartjs-2';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const MAX_VACATION_DAYS = 12;

function AbsenceDetail({ staffName, onClose }) {
  const [absences, setAbsences] = useState([]);
  const [filteredAbsences, setFilteredAbsences] = useState([]);
  const [selectedAbsenceType, setSelectedAbsenceType] = useState('ALL');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [chartData, setChartData] = useState(null);
  const [hireDate, setHireDate] = useState(null);
  const [currentPeriodStart, setCurrentPeriodStart] = useState(null);
  const [currentPeriodEnd, setCurrentPeriodEnd] = useState(null);
  const [remainingVacationDays, setRemainingVacationDays] = useState(null);
  const [filteredAbsencesList, setFilteredAbsencesList] = useState([]);

  const getCurrentPeriodDates = (hireDateString) => {
    const today = new Date();
    const hireDate = new Date(hireDateString);
    
    let periodStart = new Date(today.getFullYear(), hireDate.getMonth(), hireDate.getDate());
    
    if (today < periodStart) {
      periodStart.setFullYear(periodStart.getFullYear() - 1);
    }

    return { start: periodStart, end: today };
  };

  const calculateRemainingVacationDays = useCallback((absencesList, start, end) => {
    const totalAbsences = absencesList.filter(a =>
      a.date >= start && a.date <= end
    ).length;

    const remaining = Math.max(0, MAX_VACATION_DAYS - totalAbsences);
    setRemainingVacationDays(remaining);
  }, []);

  const calculateAbsences = useCallback((absencesList, start, end) => {
    calculateRemainingVacationDays(absencesList, start, end);
  }, [calculateRemainingVacationDays]);

  const updateChartData = useCallback((absencesList) => {
    const quarterlyData = [0, 0, 0, 0];
    absencesList.forEach(absence => {
      if (absence.date.getFullYear().toString() === year) {
        const quarter = Math.floor(absence.date.getMonth() / 3);
        if (selectedAbsenceType === 'ALL' || absence.reason.toUpperCase() === selectedAbsenceType) {
          quarterlyData[quarter]++;
        }
      }
    });

    setChartData({
      labels: ['Q1', 'Q2', 'Q3', 'Q4'],
      datasets: [
        {
          label: `${selectedAbsenceType} Absences`,
          data: quarterlyData,
          fill: false,
          borderColor: 'rgb(75, 192, 192)',
          tension: 0.1
        }
      ]
    });
  }, [selectedAbsenceType, year]);

  const fetchStaffInfo = useCallback(async () => {
    const staffRef = collection(db, 'staff');
    const q = query(staffRef, where("name", "==", staffName));
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      const staffData = querySnapshot.docs[0].data();
      const hireDateObj = staffData.hireDate.toDate();
      const hireDateString = hireDateObj.toLocaleDateString();
      setHireDate(hireDateString);
    }
  }, [staffName]);

  const fetchAbsences = useCallback(async (start, end) => {
    const absencesRef = collection(db, 'staffabsences');
    const q = query(absencesRef, where("name", "==", staffName));
    const querySnapshot = await getDocs(q);
    const absencesList = querySnapshot.docs.map(doc => {
      const data = doc.data();
      let date;
      if (data.date && typeof data.date.toDate === 'function') {
        date = data.date.toDate();
      } else if (data.date && data.date.seconds) {
        date = new Date(data.date.seconds * 1000);
      } else if (data.date) {
        date = new Date(data.date);
      } else {
        date = new Date();
        console.warn(`Invalid date for document ${doc.id}`);
      }
      return {
        id: doc.id,
        ...data,
        date: date
      };
    });

    setAbsences(absencesList);
    const filtered = absencesList.filter(absence => 
      absence.date >= start && absence.date <= end
    );
    setFilteredAbsences(filtered);
    setFilteredAbsencesList(filtered);
    updateChartData(filtered);
    calculateAbsences(filtered, start, end);
  }, [staffName, updateChartData, calculateAbsences]);

  useEffect(() => {
    fetchStaffInfo();
  }, [fetchStaffInfo]);

  useEffect(() => {
    if (hireDate) {
      const { start, end } = getCurrentPeriodDates(hireDate);
      setCurrentPeriodStart(start);
      setCurrentPeriodEnd(end);
      setStartDate(start);
      setEndDate(end);
      fetchAbsences(start, end);
    }
  }, [hireDate, fetchAbsences]);

  useEffect(() => {
    if (currentPeriodStart && currentPeriodEnd) {
      calculateAbsences(filteredAbsences, currentPeriodStart, currentPeriodEnd);
    }
  }, [filteredAbsences, currentPeriodStart, currentPeriodEnd, calculateAbsences]);

  const handleAbsenceTypeChange = (type) => {
    setSelectedAbsenceType(type);
    updateChartData(filteredAbsences);
    
    if (type === 'ALL') {
      setFilteredAbsencesList(filteredAbsences);
    } else {
      const filtered = filteredAbsences.filter(absence => 
        absence.reason.toUpperCase() === type
      );
      setFilteredAbsencesList(filtered);
    }
  };

  const handleDateFilter = () => {
    if (startDate && endDate) {
      const filtered = absences.filter(absence => 
        absence.date >= startDate && absence.date <= endDate
      );
      setFilteredAbsences(filtered);
      setFilteredAbsencesList(filtered);
      updateChartData(filtered);
      calculateAbsences(filtered, startDate, endDate);
      setCurrentPeriodStart(startDate);
      setCurrentPeriodEnd(endDate);
    }
  };

  const handleYearChange = (e) => {
    setYear(e.target.value);
    updateChartData(filteredAbsences);
  };

  return (
    <div className="bg-white shadow-md rounded px-8 pt-6 pb-12 mb-4 max-w-4xl mx-auto">
      <h2 className="text-3xl font-bold mb-6 text-center text-gray-800">{staffName}</h2>
      <p className="text-lg mb-6 text-center text-gray-600">Hired On: {hireDate || 'Loading...'}</p>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="absenceType">
            Absence Type
          </label>
          <select
            id="absenceType"
            value={selectedAbsenceType}
            onChange={(e) => handleAbsenceTypeChange(e.target.value)}
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          >
            <option value="ALL">All Absences</option>
            <option value="COVID">COVID Absences</option>
            <option value="VACATION">Vacation Absences</option>
            <option value="EXCUSED">Excused Absences</option>
            <option value="UNEXCUSED">Unexcused Absences</option>
          </select>
        </div>

        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="year">
            Year
          </label>
          <div className="flex">
            <input
              id="year"
              type="number"
              value={year}
              onChange={handleYearChange}
              className="shadow appearance-none border rounded-l w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            />
            <button
              onClick={() => updateChartData(filteredAbsences)}
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-r focus:outline-none focus:shadow-outline"
            >
              Update
            </button>
          </div>
        </div>
      </div>

      <div className="mb-6">
        <h3 className="text-xl font-semibold mb-4 text-gray-700">Filter Dates</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">Start Date:</label>
            <DatePicker
              selected={startDate}
              onChange={(date) => setStartDate(date)}
              selectsStart
              startDate={startDate}
              endDate={endDate}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            />
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">End Date:</label>
            <DatePicker
              selected={endDate}
              onChange={(date) => setEndDate(date)}
              selectsEnd
              startDate={startDate}
              endDate={endDate}
              minDate={startDate}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            />
          </div>
          <div className="flex items-end">
            <button 
              onClick={handleDateFilter}
              className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline w-full"
            >
              Apply Filter
            </button>
          </div>
        </div>
      </div>

      <AbsenceList absences={filteredAbsencesList} />

      <div className="mb-6">
        <h3 className="text-xl font-semibold mb-4 text-gray-700">Vacation Days</h3>
        <div className="bg-gray-100 rounded-lg p-4">
          <p className="text-lg mb-2">
            {remainingVacationDays !== null ? (
              remainingVacationDays > 0 ? (
                <span className="text-green-600 font-semibold">{staffName} has {remainingVacationDays} vacation day(s) available.</span>
              ) : (
                <span className="text-red-600 font-semibold">{staffName} is NOT eligible for vacation. They have used all {MAX_VACATION_DAYS} days in the given period.</span>
              )
            ) : (
              'Calculating vacation days...'
            )}
          </p>
          <p className="text-md">
            Total absences in this period: {MAX_VACATION_DAYS - remainingVacationDays}
          </p>
        </div>
      </div>

      {chartData && (
        <div className="mb-6">
          <h3 className="text-xl font-semibold mb-4 text-gray-700">Quarterly Absence Chart</h3>
          <div className="bg-white border rounded-lg p-4">
            <Line data={chartData} />
          </div>
        </div>
      )}

      <div className="flex justify-center mt-8">
        <button
          onClick={onClose}
          className="bg-red-500 hover:bg-red-700 text-white font-bold py-3 px-6 rounded focus:outline-none focus:shadow-outline"
        >
          Close
        </button>
      </div>
    </div>
  );
}

function AbsenceList({ absences }) {
  if (absences.length === 0) {
    return <p className="text-red-500 text-center py-4">No records found</p>;
  }

  return (
    <div className="mb-6">
      <h3 className="text-xl font-semibold mb-4 text-gray-700">Absence List</h3>
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {absences.map((absence, index) => (
            <li key={index} className="px-6 py-4 hover:bg-gray-50">
              <div className="flex items-center">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {absence.date.toLocaleDateString()}
                  </p>
                  <p className="text-sm text-gray-500 truncate">
                    {absence.reason}
                  </p>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default AbsenceDetail;
